import React from "react";
import { Link } from "react-router-dom";
import { Gauge, Layers } from "lucide-react";
import { fmtUsd } from "@/lib/format";

export const CAP_WARN_RATIO = 0.8;

export const capRatio = (s) => (s?.earning_cap_usd > 0 ? s.cap_earned / s.earning_cap_usd : 0);

export function CapAlert({ user }) {
  const capped = (user?.stakes ?? []).filter(
    (s) => s.earning_cap_usd > 0 && s.status === "active" && !s.principal_claimed
  );
  const reached = capped.filter((s) => s.capped_out);
  const near = capped.filter((s) => !s.capped_out && capRatio(s) >= CAP_WARN_RATIO);
  if (!reached.length && !near.length) return null;

  const allStopped = reached.length > 0 && reached.length === capped.length;
  const room = near.reduce((sum, s) => sum + Math.max(0, s.earning_cap_usd - s.cap_earned), 0);
  const maxPct = Math.round(Math.max(...near.map(capRatio), 0) * 100);
  const red = allStopped;

  const title = allStopped
    ? "Earning cap reached — your income has stopped"
    : reached.length
      ? `Earning cap reached on ${reached.length} package${reached.length > 1 ? "s" : ""}`
      : `You've used ${maxPct}% of your earning cap`;
  const body = allStopped
    ? "All your packages hit their maximum earnings. Make a new stake to start earning again — your principal is ready to withdraw."
    : near.length
      ? `Only ${fmtUsd(room)} of earnings left before ${near.length > 1 ? "these packages stop" : "this package stops"} earning. Re-stake now so your income never pauses.`
      : "Those packages stopped earning; your other packages keep earning. Re-stake to add more earning room.";

  return (
    <div
      data-testid="cap-alert-banner"
      className={`rounded-2xl border p-5 mb-6 flex items-center justify-between flex-wrap gap-4 ${
        red ? "border-red-500/40 bg-red-500/[0.07]" : "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 border ${
          red ? "bg-red-500/15 border-red-500/30" : "bg-[#D4AF37]/15 border-[#D4AF37]/30"}`}>
          <Gauge className={`w-5 h-5 ${red ? "text-red-400" : "gold-text"}`} />
        </div>
        <div>
          <div className={`font-display font-bold text-base ${red ? "text-red-300" : "text-white"}`} data-testid="cap-alert-title">{title}</div>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-xl" data-testid="cap-alert-body">{body}</p>
        </div>
      </div>
      <Link to="/buy" data-testid="cap-alert-restake-btn"
        className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors">
        <Layers className="w-4 h-4" /> Re-Stake Now
      </Link>
    </div>
  );
}
